import React, { useState, useCallback, useMemo } from "react";
import { Select, Spin } from "antd";
import { useLazyQuery } from "@apollo/client";
import { SEARCH_INSURANCE_COMPANIES } from "./SelectInsuranceCompany.gql";
import {
  SelectInsuranceCompanyProps,
  InsuranceCompany,
} from "./SelectInsuranceCompany.types";

const { Option } = Select;

const SelectInsuranceCompany: React.FC<SelectInsuranceCompanyProps> = ({
  value = [],
  onChange,
  placeholder = "Select Insurance Company",
  mode = "multiple",
  allowClear = true,
  style,
  className,
  disabled = false,
}) => {
  const [options, setOptions] = useState<InsuranceCompany[]>([]);

  const [searchCompanies, { loading }] = useLazyQuery<{
    searchInsuranceCompanies: InsuranceCompany[];
  }>(SEARCH_INSURANCE_COMPANIES, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setOptions(data?.searchInsuranceCompanies || []);
    },
  });

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.length >= 1) {
        searchCompanies({ variables: { searchTerm } });
      }
    },
    [searchCompanies]
  );

  const handleDropdownVisibleChange = useCallback(
    (open: boolean) => {
      if (open && options.length === 0) {
        searchCompanies({ variables: { searchTerm: "" } });
      }
    },
    [searchCompanies, options.length]
  );

  const handleChange = useCallback(
    (selectedValues: string[]) => {
      onChange?.(selectedValues);
    },
    [onChange]
  );

  const selectOptions = useMemo(
    () =>
      options.map((company) => (
        <Option key={company.id} value={company.id} label={company.name}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{company.name}</span>
            {company.number && (
              <span style={{ fontSize: 12, color: "#999" }}>
                #{company.number}
              </span>
            )}
          </div>
        </Option>
      )),
    [options]
  );

  return (
    <Select
      mode={mode}
      value={value}
      onChange={handleChange}
      onSearch={handleSearch}
      placeholder={placeholder}
      allowClear={allowClear}
      style={style}
      className={className}
      disabled={disabled}
      showSearch
      filterOption={false}
      notFoundContent={loading ? <Spin size="small" /> : null}
      optionFilterProp="label"
      maxTagCount="responsive"
      onDropdownVisibleChange={handleDropdownVisibleChange}
    >
      {selectOptions}
    </Select>
  );
};

export default SelectInsuranceCompany;

